// components/portal/InventoryUI.tsx
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown } from '@/components/ui/lucide-memo';
import { AppTooltip } from '@/components/ui/tooltip';
import clsx from 'clsx';
import { createPortal } from 'react-dom';

export const InventorySearch = memo(({
  value,
  onChange,
  placeholder = "Search...",
  alwaysOpen = false,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  /** A page header's search: the field is always there, as on ChatGPT and the Source dashboard,
   *  instead of an icon that has to be clicked open first. */
  alwaysOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isVisible = isOpen || value.trim().length > 0;

  const handleToggle = useCallback(() => {
    if (isOpen || value.trim().length > 0) {
      onChange('');
      setIsOpen(false);
    } else {
      setIsOpen(true);
      // Expanding without focusing meant the first keystrokes went nowhere and the user had to
      // click the field a second time.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen, value, onChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onChange('');
      setIsOpen(false);
    }
  }, [onChange]);

  if (alwaysOpen) {
    const label = placeholder.replace(/(\.\.\.|…)$/, '');
    return (
      <div className="relative h-8 w-[240px] max-w-full">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#AFAFAF]" size={16} />
        <input
          type="text"
          placeholder={label}
          aria-label={label}
          className="h-full w-full rounded-full border border-[#2A2A2A] bg-transparent pl-10 pr-3 text-[#E5E5E5] outline-none transition-colors placeholder:text-[#AFAFAF] focus:border-[#444444]"
          style={{ fontSize: 14 }}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center flex-row-reverse">
      <AppTooltip content="Search">
      <button
        type="button"
        onClick={handleToggle}
        aria-label="Search"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#E5E5E5] transition-colors hover:bg-white/[0.08] hover:text-white"
      >
        <Search size={18} strokeWidth={2} />
      </button>
      </AppTooltip>
      <div className={clsx(
        'overflow-hidden transition-[max-width,opacity,margin] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
        isVisible ? 'mr-1 max-w-[220px] opacity-100' : 'mr-0 max-w-0 opacity-0',
      )}>
        {/* ChatGPT's round outlined search (owner, 2026-09-28), the Source dashboard's own. */}
        <div className="relative h-9 w-[220px] rounded-full border border-[#2A2A2A]">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#AFAFAF]" size={15} />
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            className="h-full w-full appearance-none rounded-full border-0 bg-transparent pl-9 pr-3 text-[13px] text-white/85 shadow-none outline-none placeholder:text-[#AFAFAF] focus:outline-none"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
          />
        </div>
      </div>
    </div>
  );
});
InventorySearch.displayName = 'InventorySearch';

const SortOptionRow = memo(({
  option,
  isSelected,
  activeDir,
  onSelect,
  renderIcon,
}: {
  option: { label: string; field: string };
  isSelected: boolean;
  activeDir: string;
  onSelect: (field: string) => void;
  renderIcon?: (label: string) => React.ReactNode;
}) => {
  const handleClick = useCallback(() => onSelect(option.field), [onSelect, option.field]);

  return (
    <button
      type="button"
      className={clsx(
        'flex h-10 w-full items-center gap-2.5 rounded-[10px] px-2.5 text-left transition-colors',
        isSelected ? 'bg-[#262626] text-white' : 'text-white/90 hover:bg-[#232323]',
      )}
      onClick={handleClick}
    >
      <span className="text-white/90">
        {renderIcon ? renderIcon(option.label) : (isSelected ? (activeDir === 'asc' ? <ArrowUp size={16}/> : <ArrowDown size={16}/>) : <ArrowUpDown size={16}/>)}
      </span>
      <span style={{ fontSize: 14, lineHeight: '18px' }}>{option.label}</span>
    </button>
  );
});
SortOptionRow.displayName = 'SortOptionRow';

export const InventorySortDropdown = memo(({
  options,
  activeField,
  activeDir,
  onChange,
  currentLabel,
  renderIcon,
  pill = false,
}: {
  options: { label: string; field: string }[];
  activeField: string;
  activeDir: string;
  onChange: (field: string) => void;
  currentLabel: string;
  renderIcon?: (label: string) => React.ReactNode;
  /** A labelled pill ('Last updated ⌄') opening leftward, for the right side of a page header. */
  pill?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // The menu is drawn on the page, not inside the table: a short list clips whatever hangs below
  // its toolbar (owner, 2026-10-04, a one-row search cut the menu after two options).
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const inside = (target: EventTarget | null) =>
      Boolean(target instanceof Node && (dropdownRef.current?.contains(target) || menuRef.current?.contains(target)));
    const handleOutside = (e: MouseEvent) => { if (!inside(e.target)) setIsOpen(false); };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    // Pinned to the page, it would drift from its button: a scroll elsewhere or a resize closes it.
    const handleMove = (e: Event) => { if (!inside(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleMove, true);
    window.addEventListener('resize', handleMove);
    return () => {
      document.removeEventListener('mousedown', handleOutside); window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleMove, true); window.removeEventListener('resize', handleMove);
    };
  }, [isOpen]);

  const filteredOptions = useMemo(() => (
    query.trim()
      ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
      : options
  ), [options, query]);

  const handleToggleOpen = useCallback(() => {
    setAnchor(dropdownRef.current?.getBoundingClientRect() ?? null);
    setIsOpen(prev => !prev);
  }, []);

  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  const handleSelect = useCallback((field: string) => {
    onChange(field);
    setIsOpen(false);
  }, [onChange]);

  // A list the server orders itself (organisations) offers nothing to pick: no pill.
  if (options.length === 0) return null;

  return (
    <div ref={dropdownRef} className="relative">
      {pill ? (
        <button
          type="button"
          aria-label={`Sort by ${currentLabel}`}
          aria-expanded={isOpen}
          className="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[#E5E5E5] transition-colors hover:bg-white/[0.08] hover:text-white"
          style={{ fontSize: 14 }}
          onClick={handleToggleOpen}
        >
          {currentLabel}
          <ChevronDown size={16} className="text-[#AFAFAF]" />
        </button>
      ) : (
      <AppTooltip content="Sort">
      <button
        type="button"
        aria-label={`Sort by ${currentLabel}`}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#E5E5E5] transition-colors hover:bg-white/[0.08] hover:text-white"
        onClick={handleToggleOpen}
      >
        <ArrowUpDown size={18} strokeWidth={2} />
      </button>
      </AppTooltip>
      )}
      {isOpen && anchor && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          className={clsx('fixed z-[1000] overflow-hidden rounded-[20px] border border-white/[0.06] bg-[#353535] p-2 shadow-[0_18px_52px_rgba(0,0,0,0.5)]', pill ? 'w-[260px]' : 'w-[340px]')}
          style={{ top: anchor.bottom + 8, ...(pill ? { right: window.innerWidth - anchor.right } : { left: anchor.left }) }}
        >
          <div className={clsx('mb-2 h-8 items-center gap-2 rounded-full border border-white/[0.08] px-3', pill ? 'hidden' : 'flex')}>
            <Search size={15} className="text-white/45" />
            <input
              type="text"
              placeholder="Sort by..."
              className="h-7 w-full border-0 bg-transparent text-[15px] leading-[15px] text-white outline-none placeholder:text-white/35"
              value={query}
              onChange={handleQueryChange}
            />
          </div>
          <div className="max-h-[300px] overflow-y-auto custom-scrollbar space-y-0.5">
            {filteredOptions.map((option) => (
              <SortOptionRow
                key={option.field}
                option={option}
                isSelected={activeField === option.field}
                activeDir={activeDir}
                onSelect={handleSelect}
                renderIcon={renderIcon}
              />
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
});
InventorySortDropdown.displayName = 'InventorySortDropdown';